import { MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'

function MapCard({ children, caption }: { children: React.ReactNode; caption?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="size-5" />
          Location
        </CardTitle>
      </CardHeader>
      <CardContent>
        {children}
        {caption && <p className="text-muted-foreground mt-2 text-sm">{caption}</p>}
      </CardContent>
    </Card>
  )
}

function MapFrame({ latitude, longitude }: { latitude: number; longitude: number }) {
  const bbox = `${longitude - 0.01},${latitude - 0.01},${longitude + 0.01},${latitude + 0.01}`
  const marker = `${latitude},${longitude}`

  return (
    <div className="overflow-hidden rounded-lg">
      <iframe
        title="Violation location"
        width="100%"
        height="350"
        style={{ border: 0 }}
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`}
      />
    </div>
  )
}

export function LocationMap({ latitude, longitude }: { latitude: number; longitude: number }) {
  return (
    <MapCard caption={`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`}>
      <MapFrame latitude={latitude} longitude={longitude} />
    </MapCard>
  )
}

export function LocationMapByAddress({
  location,
  city,
  state,
}: {
  location?: string
  city?: string
  state?: string
}) {
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [loading, setLoading] = useState(true)

  const query = [location, city, state].filter(Boolean).join(', ')

  useEffect(() => {
    if (!query) {
      setLoading(false)
      return
    }
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      .then((res) => res.json())
      .then((results) => {
        if (results.length > 0) {
          setCoords({ lat: parseFloat(results[0].lat), lon: parseFloat(results[0].lon) })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [query])

  if (loading) {
    return (
      <MapCard>
        <SkeletonGroup>
          <Skeleton className="h-[350px] w-full" />
        </SkeletonGroup>
      </MapCard>
    )
  }

  if (!coords) return null

  return (
    <MapCard caption={query}>
      <MapFrame latitude={coords.lat} longitude={coords.lon} />
    </MapCard>
  )
}
